/**
 * Clickable card used in the /coaches standalone grid and the
 * project-scoped PersonaPickerModal (added in Commit E).
 *
 * When `projectId` is provided (project picker context), the card
 * navigates into /projects/{id}/chat/{personaId}; otherwise it defaults
 * to the standalone /coaches/chat/{personaId} path. This keeps the
 * component reusable in both flows without prop-drilling a navigation
 * target.
 *
 * `autoFocus` is forwarded to the underlying button so callers
 * (notably PersonaPickerModal) can place keyboard focus on the first
 * card when the modal opens — without it, focus would stay on the
 * trigger button behind the backdrop, an accessibility regression
 * introduced when Modal.jsx stopped force-focusing its close button.
 */

import { useNavigate } from "react-router-dom";
import Avatar from "./Avatar.jsx";
import { useChatNavigation } from "../hooks/useChatNavigation.js";

export default function PersonaCard({
  persona,
  projectId = null,
  autoFocus = false,
  onSelect = null,
}) {
  const navigate = useNavigate();
  const { chatPath } = useChatNavigation(projectId);

  const handleClick = () => {
    if (onSelect) {
      onSelect(persona);
      return;
    }
    navigate(chatPath(persona.id));
  };

  return (
    <button
      type="button"
      className="persona-card"
      onClick={handleClick}
      autoFocus={autoFocus}
    >
      <div className="persona-card-header">
        <Avatar persona={persona} />
        <div>
          <div className="persona-name">{persona.name}</div>
          <span className="persona-category">{persona.category}</span>
        </div>
      </div>
      <p className="persona-positioning">
        {persona.identity?.positioning || ""}
      </p>
    </button>
  );
}
